import React, {useState, useEffect, forwardRef, useCallback} from 'react'
import {
  Card,
  Text,
  Stack,
  Flex,
  Box,
  Heading,
  TextArea,
  Spinner,
  Label,
  Tab,
  TabList,
  TabPanel,
} from '@sanity/ui'
import {set, setIfMissing} from 'sanity'
import {useClient} from 'sanity'
import {ArrayOfObjectsInputProps} from 'sanity'

interface FitmentType {
  _id: string
  name: string
}

interface ScoringInfoValue {
  _key: string
  fitment: {
    _type: 'reference'
    _ref: string
  }
  description?: string
}

const ScoringInfoInput = forwardRef<HTMLDivElement, ArrayOfObjectsInputProps>(
  (props, ref) => {
    const {value, readOnly, onChange} = props

    const [fitmentTypes, setFitmentTypes] = useState<FitmentType[]>([])
    const [descriptions, setDescriptions] = useState<Record<string, string>>({})
    const [activeFitmentId, setActiveFitmentId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const client = useClient({apiVersion: '2023-05-03'})

    useEffect(() => {
      let mounted = true

      const fetchFitmentTypes = async () => {
        try {
          setIsLoading(true)
          const result = await client.fetch<FitmentType[]>(
            `*[_type == 'fitment']{ _id, name }`,
          )
          if (mounted) {
            const sorted = result.sort((a, b) => a.name.localeCompare(b.name))
            setFitmentTypes(sorted)
            if (sorted.length && !activeFitmentId) {
              setActiveFitmentId(sorted[0]._id)
            }
          }
        } catch (err) {
          if (mounted) {
            setError('Failed to load fitment types')
            console.error('Error fetching fitment types:', err)
          }
        } finally {
          if (mounted) {
            setIsLoading(false)
          }
        }
      }

      fetchFitmentTypes()

      return () => {
        mounted = false
      }
    }, [client])

    const convertArrayToMap = (arr: ScoringInfoValue[]) => {
      const map: Record<string, string> = {}
      if (!arr || !Array.isArray(arr)) return map
      arr.forEach((item) => {
        if (!item.fitment?._ref) return
        map[item.fitment._ref] = item.description || ''
      })
      return map
    }

    const convertMapToArray = (
      map: Record<string, string>,
    ): ScoringInfoValue[] =>
      Object.keys(map)
        .filter((fitmentId) => map[fitmentId] && map[fitmentId].length > 0)
        .map((fitmentId) => ({
          _key: fitmentId,
          fitment: {
            _type: 'reference',
            _ref: fitmentId,
          },
          description: map[fitmentId],
        }))

    useEffect(() => {
      if (value && Array.isArray(value)) {
        setDescriptions(convertArrayToMap(value as ScoringInfoValue[]))
      } else {
        onChange(setIfMissing([]))
      }
    }, [value, onChange])

    const handleDescriptionChange = useCallback(
      (fitmentId: string, newValue: string) => {
        const updated = {...descriptions, [fitmentId]: newValue}
        setDescriptions(updated)
        onChange(set(convertMapToArray(updated)))
      },
      [descriptions, onChange],
    )

    return (
      <Stack gap={3} ref={ref}>
        {isLoading ? (
          <Flex align="center" justify="center" padding={4}>
            <Spinner muted />
            <Box marginLeft={3}>
              <Text>Loading fitment categories...</Text>
            </Box>
          </Flex>
        ) : error ? (
          <Card tone="critical" padding={4}>
            <Text>{error}</Text>
          </Card>
        ) : (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Heading size={1}>Scoring Description per Category</Heading>
              <Text size={1} muted>
                Each fitment category can have its own description shown in the info modal.
              </Text>

              <TabList gap={2}>
                {fitmentTypes.map((fitment) => (
                  <Tab
                    key={fitment._id}
                    id={`tab-${fitment._id}`}
                    aria-controls={`panel-${fitment._id}`}
                    label={fitment.name}
                    onClick={() => setActiveFitmentId(fitment._id)}
                    selected={activeFitmentId === fitment._id}
                  />
                ))}
              </TabList>

              {fitmentTypes.map((fitment) => (
                <TabPanel
                  key={fitment._id}
                  id={`panel-${fitment._id}`}
                  aria-labelledby={`tab-${fitment._id}`}
                  hidden={activeFitmentId !== fitment._id}
                >
                  <Stack gap={2}>
                    <Label size={1}>{fitment.name}</Label>
                    <TextArea
                      rows={5}
                      value={descriptions[fitment._id] || ''}
                      placeholder={`Description shown next to "Scoring" on ${fitment.name} fitments`}
                      onChange={(event) =>
                        handleDescriptionChange(
                          fitment._id,
                          event.currentTarget.value,
                        )
                      }
                      disabled={readOnly}
                    />
                  </Stack>
                </TabPanel>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    )
  },
)

ScoringInfoInput.displayName = 'ScoringInfoInput'

export default ScoringInfoInput
